import { useCallback, useEffect, useState } from 'react';
import { MarkdownEditor } from '../../components/MarkdownEditor';
import { MarkdownPreview } from '../../components/MarkdownPreview';
import { apiService } from '../../services/api';
import type {
  ChatMessagesContact,
  ChatMessagesFlowMessages,
  ChatMessagesUiLabels,
  NgWords,
} from '../../types';
import { NgWordSection } from './NgWordSection';

// ---------------------------------------------------------------------------
// ラベル定数
// ---------------------------------------------------------------------------

const CHAT_MESSAGE_GROUPS: {
  title: string;
  fields: { key: string; label: string; source: 'flow' | 'ui'; markdown?: boolean }[];
}[] = [
  {
    title: '共通設定',
    fields: [
      { key: 'headerTitle', label: 'ヘッダータイトル', source: 'ui' },
      { key: 'buttonOpenChat', label: 'チャット起動ボタン', source: 'ui', markdown: true },
    ],
  },
  {
    title: 'ウェルカム',
    fields: [
      { key: 'welcomeGreeting', label: 'ウェルカムメッセージ', source: 'ui', markdown: true },
      {
        key: 'productCategorySelect',
        label: '製品カテゴリ選択メッセージ',
        source: 'flow',
        markdown: true,
      },
    ],
  },
  {
    title: 'FAQ・質問選択',
    fields: [
      { key: 'initialFaq', label: 'FAQ案内メッセージ', source: 'flow', markdown: true },
      {
        key: 'faqCategorySelect',
        label: 'FAQカテゴリ選択メッセージ',
        source: 'flow',
        markdown: true,
      },
      { key: 'questionSelect', label: '質問選択メッセージ', source: 'flow', markdown: true },
      { key: 'faqModalSelectFromCategory', label: 'モーダルタイトル', source: 'ui' },
    ],
  },
  {
    title: '症状検索',
    fields: [
      {
        key: 'freeTextPrompt',
        label: 'テキスト入力案内メッセージ',
        source: 'flow',
        markdown: true,
      },
      { key: 'placeholderSymptomInput', label: '症状入力欄のプレースホルダ', source: 'ui' },
      {
        key: 'placeholderCategoryRequired',
        label: 'カテゴリ未選択時のプレースホルダ',
        source: 'ui',
      },
      { key: 'symptomResult', label: '症状選択メッセージ', source: 'flow', markdown: true },
      {
        key: 'symptomSearchEmpty',
        label: '検索結果なしメッセージ',
        source: 'flow',
        markdown: true,
      },
      {
        key: 'symptomNotFound',
        label: '症状該当なし時メッセージ',
        source: 'flow',
        markdown: true,
      },
      { key: 'qaDisplayCause', label: '原因ラベル', source: 'ui' },
      { key: 'qaDisplayTreatment', label: '処置方法ラベル', source: 'ui' },
      {
        key: 'restrictedQueryResponse',
        label: 'NG ワード検出時メッセージ',
        source: 'flow',
        markdown: true,
      },
    ],
  },
  {
    title: '回答・フィードバック',
    fields: [
      {
        key: 'symptomAnswerFallback',
        label: '回答なし時のメッセージ',
        source: 'flow',
        markdown: true,
      },
      { key: 'symptomRetry', label: '再試行メッセージ', source: 'flow', markdown: true },
      { key: 'feedbackQuestion', label: 'フィードバック質問', source: 'ui' },
      { key: 'feedbackThankYou', label: '感謝メッセージ', source: 'ui' },
      { key: 'feedbackSorry', label: 'お詫びメッセージ', source: 'ui' },
      { key: 'feedbackSorryShort', label: 'お詫び（短縮）', source: 'ui' },
    ],
  },
  {
    title: '修理相談',
    fields: [
      { key: 'repairConsult', label: '修理相談メッセージ', source: 'flow', markdown: true },
      { key: 'repairContact', label: '修理連絡先メッセージ', source: 'flow', markdown: true },
    ],
  },
  {
    title: '終了・その他',
    fields: [
      { key: 'end', label: '終了メッセージ', source: 'flow', markdown: true },
      { key: 'noticeConfirmRestart', label: 'やり直し確認メッセージ', source: 'ui' },
      { key: 'placeholderMessageInput', label: 'メッセージ入力欄のプレースホルダ', source: 'ui' },
      { key: 'noticeSymptomRequired', label: '症状入力必須メッセージ', source: 'ui' },
      { key: 'noticeSymptomMaxLength', label: '文字数上限メッセージ', source: 'ui' },
    ],
  },
  {
    title: 'ボタンラベル',
    fields: [
      { key: 'buttonSubmit', label: '送信', source: 'ui' },
      { key: 'buttonYes', label: 'はい', source: 'ui' },
      { key: 'buttonNo', label: 'いいえ', source: 'ui' },
      { key: 'buttonCancel', label: 'キャンセル', source: 'ui' },
      { key: 'buttonRestart', label: 'やり直す', source: 'ui' },
      { key: 'buttonGoBack', label: '前に戻る', source: 'ui' },
      { key: 'buttonInquiryLink', label: 'お問い合わせフォーム', source: 'ui' },
      { key: 'buttonRetry', label: 'もう一度試す', source: 'ui' },
      { key: 'buttonNoMatch', label: '該当なし', source: 'ui' },
      { key: 'buttonResolved', label: '解決した', source: 'ui' },
      { key: 'buttonSearchOtherSymptom', label: '他の症状を検索', source: 'ui' },
    ],
  },
];

// ---------------------------------------------------------------------------
// ヘルパー関数
// ---------------------------------------------------------------------------

/** オブジェクトから動的キーで文字列値を取得 */
function getStringValue(obj: Record<string, unknown>, key: string): string {
  const val = obj[key];
  return typeof val === 'string' ? val : '';
}

// ---------------------------------------------------------------------------
// ヘルパーコンポーネント
// ---------------------------------------------------------------------------

const SectionHeader: React.FC<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ title, isOpen, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex w-full items-center justify-between rounded-lg bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 hover:bg-slate-100"
  >
    <span>{title}</span>
    <span className="text-slate-400">{isOpen ? '▲' : '▼'}</span>
  </button>
);

// ---------------------------------------------------------------------------
// メインコンポーネント
// ---------------------------------------------------------------------------

export const ChatMessagesPage: React.FC = () => {
  const [flowMessages, setFlowMessages] = useState<ChatMessagesFlowMessages | null>(null);
  const [uiLabels, setUiLabels] = useState<ChatMessagesUiLabels | null>(null);
  const [contact, setContact] = useState<ChatMessagesContact | null>(null);

  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');

  const [ngWords, setNgWords] = useState<NgWords>({
    personal: [],
    competitor: [],
    inappropriate: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [icons, setIcons] = useState<{ avatarIconUrl?: string; headerIconUrl?: string }>({});
  const [uploadingIcon, setUploadingIcon] = useState<'avatar' | 'header' | null>(null);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    Object.fromEntries(CHAT_MESSAGE_GROUPS.map((g) => [g.title, true])),
  );

  // ── データ読み込み ──────────────────────────────────────────
  useEffect(() => {
    setIsLoading(true);
    apiService
      .getChatMessages()
      .then((data) => {
        setFlowMessages(data.flowMessages);
        setUiLabels(data.uiLabels);
        setContact(data.contact);
        setUpdatedAt(data.updatedAt);
        setUpdatedBy(data.updatedBy);
        setIcons(data.icons ?? {});
        setNgWords(data.ngWords ?? { personal: [], competitor: [], inappropriate: [] });
      })
      .catch(() => setError('データの読み込みに失敗しました'))
      .finally(() => setIsLoading(false));
  }, []);

  // ── beforeunload ガード ─────────────────────────────────────
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── トースト自動非表示 ──────────────────────────────────────
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // ── セクション開閉 ─────────────────────────────────────────
  const toggleSection = useCallback((key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ── フィールド更新 ──────────────────────────────────────────
  const updateField = useCallback((key: string, value: string, source: 'flow' | 'ui') => {
    if (source === 'flow') {
      setFlowMessages((prev) => (prev ? { ...prev, [key]: value } : prev));
    } else {
      setUiLabels((prev) => (prev ? { ...prev, [key]: value } : prev));
    }
    setIsDirty(true);
  }, []);

  // ── アイコンアップロード ────────────────────────────────────
  const handleIconUpload = async (type: 'avatar' | 'header', file: File) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setToast({ type: 'error', message: 'PNG / JPG / GIF ファイルを選択してください' });
      return;
    }
    if (file.size > 1024 * 1024) {
      setToast({ type: 'error', message: 'ファイルサイズは 1MB 以下にしてください' });
      return;
    }

    setUploadingIcon(type);
    try {
      const { uploadUrl, fields, iconUrl } = await apiService.getIconUploadUrl({
        type,
        contentType: file.type as 'image/png' | 'image/jpeg' | 'image/gif',
      });

      // S3 presigned POST でアップロード（サーバーサイドで 1MB 制限を強制）
      const formData = new FormData();
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
      formData.append('file', file);

      const uploadRes = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error('Upload failed');
      }

      const key = type === 'avatar' ? 'avatarIconUrl' : 'headerIconUrl';
      setIcons((prev) => ({ ...prev, [key]: iconUrl }));
      setIsDirty(true);
      setToast({
        type: 'success',
        message: 'アイコンをアップロードしました。「保存」を押して反映してください',
      });
    } catch {
      setToast({ type: 'error', message: 'アイコンのアップロードに失敗しました' });
    } finally {
      setUploadingIcon(null);
    }
  };

  const handleIconReset = (type: 'avatar' | 'header') => {
    const key = type === 'avatar' ? 'avatarIconUrl' : 'headerIconUrl';
    setIcons((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setIsDirty(true);
  };

  // ── 保存 ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!flowMessages || !uiLabels || !contact) return;
    setIsSaving(true);
    setError(null);
    try {
      const result = await apiService.updateChatMessages({
        flowMessages,
        uiLabels,
        contact,
        icons,
        ngWords,
      });
      setUpdatedAt(result.updatedAt);
      setUpdatedBy(result.updatedBy);
      setIsDirty(false);
      setToast({ type: 'success', message: '保存しました' });
    } catch {
      setToast({ type: 'error', message: '保存に失敗しました' });
    } finally {
      setIsSaving(false);
    }
  };

  // ── ローディング / エラー ──────────────────────────────────
  if (isLoading) return <div className="text-slate-500">読み込み中...</div>;
  if (error && !flowMessages)
    return <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  if (!flowMessages || !uiLabels) return null;

  // ── レンダリング ───────────────────────────────────────────
  return (
    <div className="mx-auto max-w-5xl">
      {/* ヘッダー */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">チャットメッセージ設定</h1>
        <button
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isSaving ? '保存中...' : '保存'}
        </button>
      </div>

      {/* トースト */}
      {toast && (
        <div
          className={`mb-4 rounded-lg p-3 text-sm ${
            toast.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="space-y-4">
        {/* アイコン設定セクション */}
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="p-4">
            <SectionHeader
              title="アイコン設定"
              isOpen={openSections['アイコン設定'] ?? true}
              onToggle={() => toggleSection('アイコン設定')}
            />
          </div>
          {(openSections['アイコン設定'] ?? true) && (
            <div className="border-t border-slate-200 p-6">
              <p className="mb-4 text-xs text-slate-500">
                未設定の場合はデフォルトアイコンが使用されます
              </p>
              <div className="grid grid-cols-2 gap-6">
                {(['avatar', 'header'] as const).map((type) => {
                  const key = type === 'avatar' ? 'avatarIconUrl' : 'headerIconUrl';
                  const url = icons[key];
                  const label = type === 'avatar' ? 'アバターアイコン' : 'ヘッダーアイコン';
                  const desc =
                    type === 'avatar'
                      ? 'メッセージ横に表示されるアイコン'
                      : 'チャットウィンドウ上部に表示されるアイコン';

                  return (
                    <div key={type} className="rounded-lg border border-slate-200 p-5">
                      <div className="mb-1 text-sm font-semibold text-slate-700">{label}</div>
                      <div className="mb-4 text-xs text-slate-500">{desc}</div>
                      <div className="flex items-center gap-5">
                        <div
                          className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 bg-white"
                          style={{
                            borderStyle: url ? 'solid' : 'dashed',
                            borderColor: url ? '#E60012' : '#ccc',
                          }}
                        >
                          {url ? (
                            <img src={url} alt={label} className="h-full w-full object-contain" />
                          ) : (
                            <span className="text-center text-xs text-slate-400">未設定</span>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="inline-flex cursor-pointer items-center rounded-md bg-red-600 px-4 py-2 text-xs font-medium text-white hover:bg-red-700">
                            {uploadingIcon === type
                              ? 'アップロード中...'
                              : url
                                ? '変更'
                                : 'ファイルを選択'}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/gif"
                              className="hidden"
                              disabled={uploadingIcon !== null}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleIconUpload(type, file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          {url && (
                            <button
                              onClick={() => handleIconReset(type)}
                              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50"
                            >
                              デフォルトに戻す
                            </button>
                          )}
                          <span className="text-xs text-slate-400">PNG / JPG / GIF, 最大 1MB</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {CHAT_MESSAGE_GROUPS.map((group) => (
          <div key={group.title} className="rounded-xl border border-slate-200 bg-white">
            <div className="p-4">
              <SectionHeader
                title={group.title}
                isOpen={openSections[group.title] ?? true}
                onToggle={() => toggleSection(group.title)}
              />
            </div>
            {openSections[group.title] && (
              <div className="space-y-5 border-t border-slate-200 p-6">
                {group.fields.map(({ key, label, source, markdown }) => {
                  const obj =
                    source === 'flow'
                      ? (flowMessages as unknown as Record<string, unknown>)
                      : (uiLabels as unknown as Record<string, unknown>);
                  return (
                    <div key={key}>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        {label}
                      </label>
                      {markdown ? (
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <MarkdownEditor
                              value={getStringValue(obj, key)}
                              onChange={(v) => updateField(key, v, source)}
                              rows={3}
                              placeholder={label}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="mb-1.5 text-xs font-medium text-slate-500">
                              プレビュー
                            </div>
                            <div
                              className="min-h-[90px] rounded-2xl rounded-tl bg-[#F2F2F7] px-4 py-3 text-[15px] leading-relaxed"
                              style={{ wordBreak: 'break-word' }}
                            >
                              <MarkdownPreview content={getStringValue(obj, key)} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={getStringValue(obj, key)}
                          onChange={(e) => updateField(key, e.target.value, source)}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={label}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* ── NG ワード設定 ─────────────────────── */}
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="p-4">
            <SectionHeader
              title="NG ワード設定"
              isOpen={openSections['NG ワード設定'] ?? true}
              onToggle={() => toggleSection('NG ワード設定')}
            />
          </div>
          {(openSections['NG ワード設定'] ?? true) && (
            <div className="space-y-6 border-t border-slate-200 p-6">
              <NgWordSection
                title="個人情報キーワード"
                words={ngWords.personal}
                onChange={(next) => {
                  setNgWords((prev) => ({ ...prev, personal: next }));
                  setIsDirty(true);
                }}
              />
              <NgWordSection
                title="他社ブランド名"
                words={ngWords.competitor}
                onChange={(next) => {
                  setNgWords((prev) => ({ ...prev, competitor: next }));
                  setIsDirty(true);
                }}
              />
              <NgWordSection
                title="不適切な言葉"
                words={ngWords.inappropriate}
                onChange={(next) => {
                  setNgWords((prev) => ({ ...prev, inappropriate: next }));
                  setIsDirty(true);
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── 最終更新情報 + 保存ボタン ─────────────────────── */}
      <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4">
        <div className="text-xs text-slate-400">
          {updatedAt && (
            <>
              最終更新: {new Date(updatedAt).toLocaleString('ja-JP')} / {updatedBy}
            </>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isSaving ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  );
};
